import { useEffect, useState } from 'react';
import { useCustomer } from '../../../lib/customer';
import { useAuth } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { PortalContainer } from '../PortalShell';

type Address = {
  id: string;
  kind: string;
  label: string | null;
  line1: string;
  line2: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string;
  is_default: boolean;
};

const REGIMEN_LABELS: Record<string, string> = {
  '601': '601 · General de Ley Personas Morales',
  '603': '603 · Personas Morales con Fines no Lucrativos',
  '605': '605 · Sueldos y Salarios e Ingresos Asimilados a Salarios',
  '612': '612 · Personas Físicas con Actividades Empresariales',
  '621': '621 · Incorporación Fiscal',
  '626': '626 · Régimen Simplificado de Confianza',
};

const CFDI_LABELS: Record<string, string> = {
  'G01': 'G01 · Adquisición de mercancías',
  'G03': 'G03 · Gastos en general',
  'I01': 'I01 · Construcciones',
  'I02': 'I02 · Mobiliario y equipo de oficina',
  'I04': 'I04 · Equipo de cómputo',
  'P01': 'P01 · Por definir',
};

export function PortalCuenta() {
  const { customer } = useCustomer();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);

  useEffect(() => {
    if (!customer) return;
    supabase
      .from('customer_addresses')
      .select('*')
      .eq('customer_id', customer.id)
      .order('is_default', { ascending: false })
      .then(({ data }) => setAddresses((data as Address[]) ?? []));
  }, [customer]);

  if (!customer) return null;

  const creditAvailable = (customer.credit_limit_mxn ?? 0) - (customer.credit_balance_mxn ?? 0);

  return (
    <PortalContainer title="Cuenta" eyebrow="Mis datos">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
        gap: 16,
      }}>
        <Section title="Datos de contacto">
          <Field label="Email (login)" value={user?.email ?? '—'} />
          <Field label="Empresa"        value={customer.business_name} />
          <Field label="Razón social"  value={customer.legal_name ?? customer.business_name} />
          <Field label="Teléfono"        value={customer.primary_phone ?? '—'} />
          <Field label="WhatsApp"        value={customer.whatsapp ?? customer.primary_phone ?? '—'} />
          <ButtonNote>
            Para actualizar datos contacta a tu agente de ventas o escríbenos por WhatsApp.
          </ButtonNote>
        </Section>

        <Section title="Datos fiscales">
          <Field label="RFC"            value={customer.rfc ?? '—'} />
          <Field label="Régimen fiscal" value={customer.regimen_fiscal ? (REGIMEN_LABELS[customer.regimen_fiscal] ?? customer.regimen_fiscal) : '—'} />
          <Field label="Uso CFDI"       value={customer.cfdi_use ? (CFDI_LABELS[customer.cfdi_use] ?? customer.cfdi_use) : '—'} />
          <ButtonNote>
            Constancia de situación fiscal vigente requerida para emitir CFDI.
          </ButtonNote>
        </Section>

        <Section title="Tier mayorista">
          <Field label="Tier" value={customer.tier_name ?? 'Sin tier asignado'} />
          {customer.tier_discount_pct != null && (
            <Field label="Margen sobre PVP" value={`${customer.tier_discount_pct}%`} highlight />
          )}
          {customer.default_markup_pct != null && (
            <Field label="Markup default a cliente" value={`${customer.default_markup_pct}%`} />
          )}
          <Field
            label="Líneas Lutron certificadas"
            value={customer.lutron_lines_certified.length === 0 ? 'Ninguna · necesitas capacitación' : customer.lutron_lines_certified.map(formatLutronLine).join(' · ')}
          />
        </Section>

        <Section title="Crédito">
          {customer.credit_limit_mxn != null && customer.credit_limit_mxn > 0 ? (
            <>
              <Field label="Crédito asignado"
                value={`$${customer.credit_limit_mxn.toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN · ${customer.credit_days ?? 0} días`} />
              <Field label="Saldo en uso"
                value={`$${(customer.credit_balance_mxn ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN`} />
              <Field label="Disponible"
                value={`$${creditAvailable.toLocaleString('es-MX', { maximumFractionDigits: 0 })} MXN`}
                highlight={creditAvailable > 0}
              />
            </>
          ) : (
            <ButtonNote>
              Aún no tienes crédito asignado. Habla con tu agente para solicitarlo.
            </ButtonNote>
          )}
        </Section>

        <Section title="Direcciones">
          {addresses.length === 0 ? (
            <ButtonNote>Sin direcciones registradas. Agrega tu primera al solicitar tu próximo pedido.</ButtonNote>
          ) : (
            addresses.map(a => (
              <div key={a.id} style={{
                padding: 12,
                marginBottom: 8,
                background: theme.bgSoft,
                borderLeft: `3px solid ${a.is_default ? theme.accent : theme.border}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: theme.textMuted,
                  }}>
                    {a.kind} {a.is_default && '· DEFAULT'}
                  </span>
                  {a.label && (
                    <span style={{ fontSize: 11, color: theme.textMuted }}>{a.label}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: theme.brand }}>
                  {a.line1}
                  {a.line2 ? `, ${a.line2}` : ''}
                  {a.neighborhood ? ` · ${a.neighborhood}` : ''}
                  {a.city ? `, ${a.city}` : ''}
                  {a.state ? `, ${a.state}` : ''}
                  {a.postal_code ? ` ${a.postal_code}` : ''}
                </div>
              </div>
            ))
          )}
        </Section>

        <Section title="Histórico">
          <Field label="Pedidos totales"      value={customer.total_orders.toLocaleString('es-MX')} />
          <Field label="Facturación histórica MXN" value={`$${customer.total_revenue_mxn.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`} />
          <Field label="Estado"                value={customer.status} />
          <Field label="Tipo de cuenta"         value={customer.customer_type} />
        </Section>

        <SecuritySection />
      </div>
    </PortalContainer>
  );
}

// ============================================================================
// Sección Seguridad — establecer/cambiar contraseña.
// Para los 122 distribuidores migrados desde BigCommerce que entraron por
// magic link y no tienen pwd. Permite establecer una para futuras entradas
// directas con email+password (sin tener que pedir magic link cada vez).
// ============================================================================

function SecuritySection() {
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (pwd.length < 8) { setErr('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (pwd !== pwd2) { setErr('Las contraseñas no coinciden.'); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setSubmitting(false);
    if (error) { setErr(error.message); return; }
    setOk(true);
    setPwd(''); setPwd2('');
  };

  return (
    <Section title="Seguridad">
      <div style={{ fontSize: 13, color: theme.textMuted, lineHeight: 1.6, marginBottom: 14 }}>
        Establece una contraseña para entrar más rápido la próxima vez. <br/>
        Si entraste hoy por magic link, esta es una alternativa permanente.
      </div>

      <form onSubmit={submit}>
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11, color: theme.textMuted, marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
          }}>
            Nueva contraseña
          </div>
          <input
            type={show ? 'text' : 'password'}
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            placeholder="Mínimo 8 caracteres"
            autoComplete="new-password"
            style={pwdInputStyle}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11, color: theme.textMuted, marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
          }}>
            Confirmar contraseña
          </div>
          <input
            type={show ? 'text' : 'password'}
            value={pwd2}
            onChange={e => setPwd2(e.target.value)}
            autoComplete="new-password"
            style={pwdInputStyle}
          />
        </div>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: theme.textMuted, cursor: 'pointer', marginBottom: 14,
        }}>
          <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
          Mostrar contraseña
        </label>

        {err && (
          <div style={{
            color: theme.err, fontSize: 12, marginBottom: 12,
            padding: 8, background: 'rgba(220,38,38,0.05)',
            border: `1px solid ${theme.err}`,
          }}>
            {err}
          </div>
        )}

        {ok && (
          <div style={{
            color: theme.brand, fontSize: 12, marginBottom: 12,
            padding: 10, background: theme.accentSoft,
            border: `1px solid ${theme.accent}`, lineHeight: 1.5,
          }}>
            ✓ Contraseña establecida. La próxima vez puedes entrar con email + contraseña directo en <code>nuled.mx/login</code>.
          </div>
        )}

        <button type="submit" disabled={submitting || pwd.length === 0} style={{
          background: theme.accent, color: theme.brand, border: 'none',
          padding: '10px 18px', fontSize: 12, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.5,
          cursor: submitting ? 'wait' : 'pointer', opacity: (submitting || pwd.length === 0) ? 0.6 : 1,
          fontFamily: theme.font.sans,
        }}>
          {submitting ? 'Guardando…' : 'Establecer contraseña'}
        </button>
      </form>
    </Section>
  );
}

const pwdInputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '10px 12px', color: theme.text, fontSize: 14,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 20 }}>
      <h2 style={{
        fontFamily: theme.font.head,
        fontSize: 16,
        fontWeight: 600,
        color: theme.brand,
        margin: '0 0 16px',
        paddingBottom: 12,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 11,
        color: theme.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: 700,
        marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 14, color: highlight ? theme.accentDark : theme.brand, fontWeight: highlight ? 700 : 400 }}>
        {value}
      </div>
    </div>
  );
}

function ButtonNote({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 12,
      padding: 10,
      background: theme.bgSoft,
      fontSize: 12,
      color: theme.textMuted,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

function formatLutronLine(l: string) {
  if (l === 'radiora3') return 'RadioRA 3';
  if (l === 'caseta') return 'Caséta';
  if (l === 'homeworks') return 'HomeWorks';
  return l.charAt(0).toUpperCase() + l.slice(1);
}
